import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

const CustomerView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`/api/customers/${id}`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success' && data.data) setCustomer(data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const genderLabel = (val) => {
    if (val === 0 || val === '0') return t('customers.male');
    if (val === 1 || val === '1') return t('customers.female');
    if (val === 2 || val === '2') return t('customers.unspecified');
    return '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400 dark:text-slate-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400">
        {t('customers.no_customers')}
      </div>
    );
  }

  const InfoRow = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 py-3 border-b border-slate-100 dark:border-slate-800">
      <div className="sm:w-40 text-sm text-slate-500 dark:text-slate-400 shrink-0">{label}</div>
      <div className="text-sm text-slate-800 dark:text-slate-200 break-all">{value || '-'}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/customers')}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
          <ArrowLeft size={20} className="text-slate-600 dark:text-slate-400" />
        </button>
        <h1 className="text-2xl font-bold dark:text-slate-100">{t('customers.customer_detail')}</h1>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InfoRow label={t('customers.id')} value={customer.id} />
            <InfoRow label={t('customers.email')} value={customer.email} />
            <InfoRow label={t('customers.firstname')} value={customer.firstname} />
            <InfoRow label={t('customers.lastname')} value={customer.lastname} />
            <InfoRow label={t('customers.gender')} value={genderLabel(customer.gender)} />
            <InfoRow label={t('customers.dob')} value={customer.dob} />
            <InfoRow label={t('customers.taxvat')} value={customer.taxvat} />
          </div>
          <div>
            <InfoRow label={t('customers.channel_type')} value={customer.channel_type} />
            <InfoRow label={t('customers.total_orders')} value={customer.total_orders} />
            <InfoRow label={t('customers.last_order_date')} value={customer.last_order_date ? customer.last_order_date.substring(0, 19) : '-'} />
            <InfoRow label={t('customers.last_pulled_at')} value={customer.last_pulled_at ? customer.last_pulled_at.substring(0, 19) : '-'} />
            <InfoRow label={t('customers.created_at')} value={customer.created_at ? customer.created_at.substring(0, 19) : '-'} />
          </div>
        </div>
      </div>

      {customer.addresses && customer.addresses.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 dark:text-slate-200">
            <MapPin size={18} className="text-slate-500" />
            {t('customers.addresses')} ({customer.addresses.length})
          </h2>
          <div className="space-y-4">
            {customer.addresses.map((addr) => (
              <div key={addr.id} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    {addr.firstname} {addr.lastname}
                  </span>
                  {addr.default_billing ? (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                      {t('customers.default_billing')}
                    </span>
                  ) : null}
                  {addr.default_shipping ? (
                    <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                      {t('customers.default_shipping')}
                    </span>
                  ) : null}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400 space-y-0.5">
                  <div>{t('customers.telephone')}: {addr.telephone || '-'}</div>
                  <div>{t('customers.address_street')}: {addr.street || '-'}</div>
                  <div>{[addr.city, addr.region, addr.postcode, addr.country_id].filter(Boolean).join(', ') || '-'}</div>
                  {addr.company && <div>{t('customers.address_company')}: {addr.company}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerView;
